// Mirrors app/models/character_profile.py (camelCase, matches the JSON the
// studio REST API returns). Kept as string-literal unions so the values line up
// exactly with the backend enums.

export type GuideCategory =
  | "companion"
  | "nutrition"
  | "mental_wellness"
  | "sleep"
  | "career"
  | "fitness"
  | "relationships"
  | "financial_wellness"
  | "productivity"
  | "mindfulness"
  | "confidence"
  | "custom";

export const GUIDE_CATEGORIES: GuideCategory[] = [
  "companion", "nutrition", "mental_wellness", "sleep", "career", "fitness",
  "relationships", "financial_wellness", "productivity", "mindfulness", "confidence", "custom",
];

export type GuideStatus = "draft" | "in_review" | "published" | "archived";
export const GUIDE_STATUSES: GuideStatus[] = ["draft", "in_review", "published", "archived"];

export type TTSProvider = "gemini" | "deepgram" | "elevenlabs" | "gcp";
export type GuideVisibility = "private" | "shared" | "public";

export type Warmth = "low" | "medium" | "high";
export type Directness = "gentle" | "balanced" | "direct";
export type Structure = "freeform" | "light" | "structured";
export type SentenceLength = "short" | "medium" | "long";
export type PauseStyle = "minimal" | "natural" | "spacious";

export const WARMTH: Warmth[] = ["low", "medium", "high"];
export const DIRECTNESS: Directness[] = ["gentle", "balanced", "direct"];
export const STRUCTURE: Structure[] = ["freeform", "light", "structured"];
export const SENTENCE_LENGTH: SentenceLength[] = ["short", "medium", "long"];
export const PAUSE_STYLE: PauseStyle[] = ["minimal", "natural", "spacious"];

export interface ResponseStyle {
  warmth: Warmth;
  directness: Directness;
  structure: Structure;
  maxSentences: number;
}

export interface SpeechStyle {
  sentenceLength: SentenceLength;
  pauseStyle: PauseStyle;
  allowEllipsis: boolean;
  allowFillers: boolean;
}

export interface CharacterTTS {
  provider: TTSProvider;
  voiceId: string;
  speed: number;
  // Deepgram voice used when the ElevenLabs voice can't serve.
  fallbackVoiceId?: string | null;
  // Gemini voice on the Gemini Live engine; required to publish.
  liveVoiceId?: string | null;
}

export interface CharacterProfile {
  id: string;
  schemaVersion: string;
  version: number;
  name: string;
  role: string;
  category: GuideCategory;
  tagline: string;
  description: string;
  imageUrl: string;
  accentColor: string | null;
  sortOrder: number;
  isDefault: boolean;

  // UI display fields (mobile app)
  speciality: string;
  profession: string;
  yearsOfExperience: number;
  rating: number;
  age: number | null;
  voiceStyle: string;
  voicePreviewUrl: string;
  experienceIn: string[];
  howItWorks: string[];
  bestFor: string[];
  // Routing (main-thread-and-guides-design §2.4): the subjects this guide
  // owns (GuideDomain ids; empty = derived from the category on save) and
  // whether Aura may hand a conversation over to it.
  domains: string[];
  handoverEligible: boolean;
  personaOverlay: string;
  knowledgePack: string;
  boundaries: string[];
  greetingVariants: string[];
  responseStyle: ResponseStyle;
  speechStyle: SpeechStyle;
  tts: CharacterTTS;
  knowledgeBaseId: string | null;
  ownerUid: string | null;
  visibility: GuideVisibility;
  shareSlug: string | null;
  derivedFromId: string | null;
  status: GuideStatus;
  enabled: boolean;
  publishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface VoiceInfo {
  provider: string;
  voiceId: string;
  name: string;
  gender?: string;
  style?: string;
}

export type VoiceCatalog = Record<string, VoiceInfo[]>;

// GET /studio/guides/domains — the routing vocabulary.
export interface GuideDomainInfo {
  id: string;
  label: string;
  // Offered guides (published, enabled, eligible, not Aura) that own it now.
  owners: string[];
}

export interface DomainCatalog {
  domains: GuideDomainInfo[];
  // Domains a guide owns when its own list is empty, by category.
  categoryDefaults: Record<string, string[]>;
}

// Aura is Main and is never offered for a hand-over, whatever its toggle says.
export const AURA_GUIDE_ID = "aura";

export function emptyProfile(): CharacterProfile {
  const now = new Date().toISOString();
  return {
    id: "",
    schemaVersion: "1.0",
    version: 1,
    name: "",
    role: "",
    category: "custom",
    tagline: "",
    description: "",
    imageUrl: "",
    accentColor: null,
    sortOrder: 0,
    isDefault: false,
    speciality: "",
    profession: "",
    yearsOfExperience: 0,
    rating: 0,
    age: null,
    voiceStyle: "",
    voicePreviewUrl: "",
    experienceIn: [],
    howItWorks: [],
    bestFor: [],
    domains: [],
    handoverEligible: true,
    personaOverlay: "",
    knowledgePack: "",
    boundaries: [],
    greetingVariants: [],
    responseStyle: { warmth: "high", directness: "balanced", structure: "light", maxSentences: 4 },
    speechStyle: { sentenceLength: "short", pauseStyle: "natural", allowEllipsis: true, allowFillers: false },
    // liveVoiceId starts empty: the editor requires a pick before publish.
    tts: { provider: "deepgram", voiceId: "aura-2-thalia-en", speed: 1.0, liveVoiceId: null },
    knowledgeBaseId: null,
    ownerUid: null,
    visibility: "public",
    shareSlug: null,
    derivedFromId: null,
    status: "draft",
    enabled: true,
    publishedAt: null,
    createdAt: now,
    updatedAt: now,
  };
}
